import { formatHumanName, MedplumClient, resolveId } from "@medplum/core";
import { Encounter, Patient } from "@medplum/fhirtypes";

import { EMPTY_VALUE } from "@/billing/constants/empty-value";
import {
  ENCOUNTER_SERVICES_ANY_CODE_TAGS,
  ENCOUNTER_SERVICES_EXCLUDED_TAGS,
  ENCOUNTER_SERVICES_NOT_SENT_TAG,
  EncounterServicesStatusCode,
} from "@/billing/constants/encounter-services-status";
import { setEncounterServicesStatus } from "@/billing/services/encounter-services-status";
import { buildSignedAndLockedByTagSearchValue } from "@/billing/services/missing-services-filter-providers";
import { resolveMissingServicesProviderDisplay } from "@/billing/services/missing-services-provider-display";
import {
  DismissMissingServiceEncounterResult,
  LoadMissingServicesCountsResult,
  LoadMissingServicesResult,
  MissingServicesFilters,
  MissingServicesSortDirection,
  MissingServicesTableRow,
  MissingServiceStatus,
} from "@/billing/types/missing-services";
import { getEncounterTypeFilterTags } from "@/billing/utils/encounter-type-filter";
import { getPatientMrn } from "@/clients/utils/mrn";
import { getEncounterServiceDateTimezone } from "@/encounters/utils/encounter-timezone";
import { toServiceDateTimezoneFormValue } from "@/encounters/utils/service-date-timezone-form";
import { QuiltedHealthCodeSystems } from "@/fhir/enums/quilted-health";
import { formatDateInTimezone } from "@/utils/datetime";
import { getCodeableConceptDisplay, getOrganizationReference } from "@/utils/medplum";

export const MISSING_SERVICES_PAGE_SIZE = 20;

const ENCOUNTER_DATE_FORMAT = "MM/dd/yyyy 'at' h:mm a";

/** Formats an Encounter's service date and time in the timezone stored on the Encounter. */
function formatEncounterDateTime(encounter: Encounter): string {
  const timezone = toServiceDateTimezoneFormValue(getEncounterServiceDateTimezone(encounter));
  return formatDateInTimezone(encounter.period?.start, timezone.code, ENCOUNTER_DATE_FORMAT);
}

/**
 * Reads the row's state off the tag the Encounter arrived with. A `services-not-sent` code means it
 * has Services with at least one unsent; no code from this system means it has none. Fully-sent
 * Encounters never reach here — the search excludes them.
 */
function resolveServiceStatus(encounter: Encounter): MissingServiceStatus {
  const isNotSent = encounter.meta?.tag?.some(
    (tag) =>
      tag.system === QuiltedHealthCodeSystems.EncounterServicesStatus &&
      tag.code === EncounterServicesStatusCode.NotSent,
  );
  return isNotSent ? "services-not-sent" : "no-services";
}

/** An Encounter plus the id and client id a table row needs. */
interface RowSource {
  encounter: Encounter;
  encounterId: string;
  clientId: string;
}

/** Returns the Encounter with its row ids, or null if it has no subject to attribute a row to. */
function toRowSource(encounter: Encounter): RowSource | null {
  const clientId = resolveId(encounter.subject);
  if (!encounter.id || !clientId) {
    return null;
  }
  return { encounter, encounterId: encounter.id, clientId };
}

/** Reads each Encounter's subject Patient once, keyed by patient id. */
async function fetchPatientsForEncounters(
  medplum: MedplumClient,
  sources: RowSource[],
): Promise<Map<string, Patient>> {
  const ids = [...new Set(sources.map((source) => source.clientId))];
  const results = await Promise.allSettled(ids.map((id) => medplum.readResource("Patient", id)));

  const patientsById = new Map<string, Patient>();
  results.forEach((result, index) => {
    if (result.status === "fulfilled") {
      patientsById.set(ids[index], result.value);
    }
  });
  return patientsById;
}

/** Resolves each Encounter's provider display once, keyed by encounter id. */
async function fetchProviderDisplaysForEncounters(
  medplum: MedplumClient,
  sources: RowSource[],
): Promise<Map<string, string>> {
  const results = await Promise.allSettled(
    sources.map((source) => resolveMissingServicesProviderDisplay(medplum, source.encounter)),
  );

  const providerDisplaysByEncounterId = new Map<string, string>();
  results.forEach((result, index) => {
    if (result.status === "fulfilled") {
      providerDisplaysByEncounterId.set(sources[index].encounterId, result.value);
    }
  });
  return providerDisplaysByEncounterId;
}

/** Builds one table row from data already prefetched for the whole page. */
function buildRow(
  source: RowSource,
  patientsById: Map<string, Patient>,
  providerDisplaysByEncounterId: Map<string, string>,
): MissingServicesTableRow {
  const { encounter, encounterId, clientId } = source;
  const patient = patientsById.get(clientId);
  const provider = providerDisplaysByEncounterId.get(encounterId) ?? "";

  return {
    encounterId,
    clientId,
    clientName: formatHumanName(patient?.name?.[0] ?? {}) || EMPTY_VALUE,
    mrn: (patient && getPatientMrn(patient)) || EMPTY_VALUE,
    encounterDate: formatEncounterDateTime(encounter) || EMPTY_VALUE,
    encounterType: getCodeableConceptDisplay(encounter.serviceType, 0, EMPTY_VALUE),
    provider: provider.trim() || EMPTY_VALUE,
    serviceStatus: resolveServiceStatus(encounter),
  };
}

/**
 * Maps the Status filter's selection to how it appears in the search. There are only two open
 * statuses, so selecting neither or both is equivalent to no filter (today's default): an object-key
 * `_tag:not` exclusion. Selecting exactly one becomes a `_filter` fragment instead of a plain `_tag`
 * object key — this lets it combine with an Encounter-type `_filter` fragment via `and` (see
 * `buildTagSearchParams`); two independent positive `_tag=` values can't both be required under the
 * same object key without one silently turning into an OR against the other.
 */
function resolveStatusClause(
  statuses: MissingServiceStatus[],
): { tagNot: string } | { filterEq: string } {
  const hasNoServices = statuses.includes("no-services");
  const hasNotSent = statuses.includes("services-not-sent");

  if (hasNoServices === hasNotSent) {
    return { tagNot: ENCOUNTER_SERVICES_EXCLUDED_TAGS };
  }
  if (hasNotSent) {
    return { filterEq: `_tag eq ${ENCOUNTER_SERVICES_NOT_SENT_TAG}` };
  }
  return { tagNot: ENCOUNTER_SERVICES_ANY_CODE_TAGS };
}

/** Builds a `_filter` OR-group across every selected Encounter-type key's tag token(s). */
function buildEncounterTypeFilterClause(encounterTypeKeys: string[]): string | undefined {
  const filterTags = getEncounterTypeFilterTags();
  const clauses = encounterTypeKeys
    .flatMap((key) => filterTags[key] ?? [])
    .map((token) => `_tag eq ${token}`);
  if (clauses.length === 0) return undefined;
  return clauses.length === 1 ? clauses[0] : `(${clauses.join(" or ")})`;
}

/**
 * Builds the Provider, Status, and Encounter-type filters' search params together — all three match
 * on `Encounter.meta.tag`, so they have to be assembled in one place to combine correctly.
 *
 * `_tag:not` (an object key) carries the default/negative exclusion case; `_filter` carries any
 * positive requirement, combining a single-status match with an Encounter-type OR-group via `and` when
 * both are active — the same "field eq A and (field eq B or field eq C)" pattern already used in
 * `client-results.ts` and `export-labs.ts` for a different token field. The Provider filter takes the
 * plain `_tag` object key: a separate key from `_tag:not` and `_filter`, so the server ANDs it with
 * whichever of those are present, while its own comma-joined values stay an OR-list across providers.
 */
function buildTagSearchParams(filters: MissingServicesFilters | undefined): {
  "_tag:not"?: string;
  _tag?: string;
  _filter?: string;
} {
  const statusClause = resolveStatusClause(filters?.statuses ?? []);
  const encounterTypeClause = buildEncounterTypeFilterClause(filters?.encounterTypeKeys ?? []);
  const signedAndLockedByTag = buildSignedAndLockedByTagSearchValue(filters?.providerIds ?? []);

  const filterParts: string[] = [];
  if ("filterEq" in statusClause) filterParts.push(statusClause.filterEq);
  if (encounterTypeClause) filterParts.push(encounterTypeClause);

  return {
    ...("tagNot" in statusClause ? { "_tag:not": statusClause.tagNot } : {}),
    ...(signedAndLockedByTag ? { _tag: signedAndLockedByTag } : {}),
    ...(filterParts.length > 0 ? { _filter: filterParts.join(" and ") } : {}),
  };
}

/**
 * Loads one page of the practice's Encounters that still need Services, oldest first. That covers
 * both states: Encounters with no Services at all, and Encounters whose Services are not all sent.
 *
 * One search finds both, by asking for everything not tagged `services-sent` or `dismissed`. Because
 * the filter is in the search, `_total: "accurate"` counts the matching Encounters rather than all of
 * them. So `page` maps to a real offset, and every page is full except the last.
 *
 * `page` is 1-based. `sortDirection` sorts Date of Encounter — the only column FHIR search can sort
 * accurately — defaulting to oldest-first when omitted.
 */
export async function loadMissingServices(
  medplum: MedplumClient,
  practiceId: string,
  page: number,
  filters?: MissingServicesFilters,
  sortDirection?: MissingServicesSortDirection,
): Promise<LoadMissingServicesResult> {
  if (!practiceId.trim()) {
    return { ok: false, reason: "missing_practice_id" };
  }

  const tagSearchParams = buildTagSearchParams(filters);
  const sort = sortDirection === "desc" ? "-date" : "date";

  try {
    const encounters = await medplum.searchResources(
      "Encounter",
      {
        _compartment: getOrganizationReference(practiceId),
        ...tagSearchParams,
        _sort: sort,
        _count: MISSING_SERVICES_PAGE_SIZE,
        _offset: (page - 1) * MISSING_SERVICES_PAGE_SIZE,
        _total: "accurate",
        status: "finished",
      },
      { cache: "no-cache" },
    );

    const sources = encounters
      .map(toRowSource)
      .filter((source): source is RowSource => source !== null);
    const [patientsById, providerDisplaysByEncounterId] = await Promise.all([
      fetchPatientsForEncounters(medplum, sources),
      fetchProviderDisplaysForEncounters(medplum, sources),
    ]);
    const rows = sources.map((source) =>
      buildRow(source, patientsById, providerDisplaysByEncounterId),
    );

    return {
      ok: true,
      data: rows,
      totalRows: encounters.bundle.total ?? 0,
    };
  } catch (error) {
    return {
      ok: false,
      reason: "load_failed",
      details: error instanceof Error ? error.message : String(error),
    };
  }
}

/** Runs a count-only Encounter search for the practice and returns the server's total. */
async function countEncounters(
  medplum: MedplumClient,
  practiceId: string,
  tagFilter: Record<string, string>,
): Promise<number> {
  const summary = await medplum.searchResources(
    "Encounter",
    {
      _compartment: getOrganizationReference(practiceId),
      ...tagFilter,
      _summary: "count",
      _total: "accurate",
    },
    { cache: "no-cache" },
  );
  return summary.bundle.total ?? 0;
}

/**
 * Loads the practice-wide counts behind the summary cards. These are separate from the table's query
 * on purpose: the cards report what the practice owes in total, so paging must not change them.
 *
 * Three count-only searches, one per number. `openItems` uses the same predicate as the table,
 * `servicesNotSent` matches its code directly, and `noServices` excludes every known code so it counts
 * untagged Encounters directly, not a difference between two separate live counts. Two live counts can
 * disagree with each other by the time both come back, so a derived `noServices` could go negative;
 * counting it directly cannot.
 */
export async function loadMissingServicesCounts(
  medplum: MedplumClient,
  practiceId: string,
): Promise<LoadMissingServicesCountsResult> {
  if (!practiceId.trim()) {
    return { ok: false, reason: "missing_practice_id" };
  }

  try {
    const [openItems, servicesNotSent, noServices] = await Promise.all([
      countEncounters(medplum, practiceId, { "_tag:not": ENCOUNTER_SERVICES_EXCLUDED_TAGS }),
      countEncounters(medplum, practiceId, { _tag: ENCOUNTER_SERVICES_NOT_SENT_TAG }),
      countEncounters(medplum, practiceId, { "_tag:not": ENCOUNTER_SERVICES_ANY_CODE_TAGS }),
    ]);

    return {
      ok: true,
      data: { noServices, servicesNotSent, openItems },
    };
  } catch (error) {
    return {
      ok: false,
      reason: "load_failed",
      details: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Tags an Encounter `dismissed`, replacing any existing code from this CodeSystem so it carries only
 * one. Once written, the Encounter no longer matches the Missing Services queue's search.
 */
export async function dismissMissingServiceEncounter(
  medplum: MedplumClient,
  encounterId: string,
): Promise<DismissMissingServiceEncounterResult> {
  const result = await setEncounterServicesStatus(
    medplum,
    encounterId,
    EncounterServicesStatusCode.Dismissed,
  );

  // The writer names its failure after the write; this names it after what the user asked for, which
  // is what the queue reports back to them.
  return result.ok ? result : { ok: false, reason: "dismiss_failed", details: result.details };
}
